import { c as _c } from "react/compiler-runtime";
import { useField, useFormikContext } from 'formik';
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import useSWR from 'swr';
import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FilterSection } from '../../../../../../../../typings/AICompletion.interface';
import { Rule } from '../../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import HookdeckAPI from '../../../../../client/Hookdeck';
import { capitalizeFirstLetter, fieldName } from '../../../../../utils';
import { getMsIntervalByUnit, postprocessFormRules, preprocessFormRules, RETRY_STATEGY_LABELS, RULE_TYPES, validateFormRules } from '../../../../../utils/rules';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Loading from '../../../../common/base/Loading';
import Tabs from '../../../../common/base/Tabs';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import Dropdown from '../../../../common/Dropdown';
import DropdownMenu from '../../../../common/DropdownMenu';
import useEventPayloadFieldsSuggestions from '../../../../common/Form/Fields/hooks/useEventPayloadFieldsSuggestions';
import IntervalInput from '../../../../common/Form/Fields/IntervalInput';
import JSONFieldSelectorInput from '../../../../common/Form/Fields/JSONFieldSelectorInput';
import SelectInput, { StyledSelect } from '../../../../common/Form/Fields/SelectInput';
import Switch from '../../../../common/Form/Fields/Switch';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import FilterRuleForm from './FilterRuleForm';
import TransformRuleFormWrapper from './TransformRuleForm';
import FilterEditorInput, { isFilterDescription } from './FilterEditorInput';
import { FILTER_SECTIONS } from './useAIFilter';
const DRAGGABLE_RULES = ['deduplicate', 'transform', 'filter'];
const getRenderingOrder = (draggable_rules_order: string[] = []) => {
  const render_order: string[] = [];

  // Add draggable rules in the order specified by draggableRulesOrder
  render_order.push(...draggable_rules_order);

  // Always add delay and retry
  render_order.push('delay', 'retry');
  return render_order;
};
export const getOrderedRules = (rules: Rule[] = []) => {
  const draggable_rules_order = rules.map(rule => rule.type).filter(type => DRAGGABLE_RULES.includes(type));
  const rendering_order = getRenderingOrder(draggable_rules_order);
  return rules.sort((a, b) => {
    const a_index = rendering_order.indexOf(a.type);
    const b_index = rendering_order.indexOf(b.type);
    return a_index - b_index;
  });
};
export const rules_default = {
  retry: {},
  // Default set on first render in RetryRuleInputs since we need to use a hook to get local storage
  delay: {},
  // Default set on first render in DelayRuleInputs since we need to use a hook to get local storage
  filter: {},
  transform: {
    input: JSON.stringify({
      headers: {},
      body: {},
      query: '',
      path: '/'
    }, null, 2),
    transformation: {
      name: '',
      env: [],
      code: `addHandler('transform', (request, context) => {
  // Transform the request object then return it.
  return request;
});`
    }
  },
  deduplicate: {
    window: 60000,
    // 1 minute
    field_mode: 'all'
  }
};
type Display = 'full' | 'compact';

/**
 * Converts an array of HTTP code rules into a human-readable sentence.
 *
 * This function processes the rules in the following order:
 * 1. Exact inclusions (e.g., 404)
 * 2. Range inclusions (e.g., "200-299")
 * 3. Comparison inclusions (e.g., ">=300")
 * 4. Exclusions (e.g., "!401")
 *
 * Example usage:
 * response_status_codes = [">=300", "!429", "!401", "201-299"];
 * Output: "Retry on between 201 and 299, greater than or equal to 300 but not 429 or 401"
 *
 * It then combines these into a sentence, listing inclusions first, followed by exclusions.
 *
 * @param httpCodeRules An array of rules to convert.
 * @returns A human-readable string describing the retry conditions.
 */
const httpCodesToSentence = (response_status_codes: string | null): string => {
  if (!response_status_codes) {
    return 'any error';
  }
  const rules = response_status_codes.split(',').map(rule => rule.trim());
  const inclusions: string[] = [];
  const exclusions: string[] = [];
  const exact_in_inclusions: string[] = [];
  const valid_rule_pattern = /^(!?\d{3}|[<>]=?\d{3}|\d{3}-\d{3})$/;
  rules.forEach(rule => {
    if (!valid_rule_pattern.test(rule)) {
      return; // Skip invalid rules
    }
    if (/^\d{3}$/.test(rule)) {
      exact_in_inclusions.push(rule);
    } else if (rule.startsWith('!')) {
      exclusions.push(rule.slice(1));
    } else if (rule.includes('-')) {
      const [min, max] = rule.split('-').map(Number);
      inclusions.push(`between ${min} and ${max}`);
    } else {
      const match = rule.match(/^(>|>=|<|<=)(\d+)$/);
      if (match) {
        const [, operator, value] = match;
        const readableOperator = {
          '>': 'greater than',
          '>=': 'greater than or equal to',
          '<': 'less than',
          '<=': 'less than or equal to'
        }[operator];
        inclusions.push(`${readableOperator} ${value}`);
      }
    }
  });

  // If there are no valid rules, return "any error"
  if (inclusions.length === 0 && exclusions.length === 0 && exact_in_inclusions.length === 0) {
    return 'any error';
  }
  if (exact_in_inclusions.length > 0) {
    inclusions.unshift(exact_in_inclusions.join(', '));
  }
  let sentence = '';
  if (exclusions.length > 0 && inclusions.length === 0) {
    // Only exclusions: check if any 2XX codes are excluded
    const has2xxExclusion = exclusions.some(code => {
      const numCode = parseInt(code);
      return numCode >= 200 && numCode < 300;
    });

    // "all HTTP codes (including 2XX) except X" when no 2XX are excluded
    sentence = `all HTTP codes${has2xxExclusion ? '' : ' (including 2XX)'} except ${exclusions.join(' or ')}`;
  } else {
    // Has inclusions (with or without exclusions)
    sentence = 'HTTP codes ';
    if (inclusions.length > 0) {
      sentence += inclusions.join(', ');
    } else {
      sentence += 'all status codes';
    }
    if (exclusions.length > 0) {
      sentence += ` but not ${exclusions.join(' or ')}`;
    }
  }
  return sentence;
};
const RetryRuleInputs: React.FC<{
  name: string;
}> = t0 => {
  const $ = _c(80);
  const {
    name
  } = t0;
  const {
    values,
    resetForm,
    dirty
  } = useFormikContext();
  const [t1, t2, t3] = useField(name);
  const {
    value
  } = t1;
  const {
    touched
  } = t2;
  const {
    setValue
  } = t3;
  let t4;
  if ($[0] !== name) {
    t4 = fieldName("strategy", name);
    $[0] = name;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const [t5,, t6] = useField(t4);
  const {
    value: strategy_value
  } = t5;
  const {
    setValue: setStrategyValue
  } = t6;
  let t7;
  if ($[2] !== name) {
    t7 = fieldName("interval", name);
    $[2] = name;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const [t8] = useField(t7);
  const {
    value: interval_value
  } = t8;
  let t9;
  if ($[4] !== name) {
    t9 = fieldName("count", name);
    $[4] = name;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const [t10] = useField(t9);
  const {
    value: count_value
  } = t10;
  let t11;
  if ($[6] !== name) {
    t11 = fieldName("response_status_codes", name);
    $[6] = name;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const [t12] = useField(t11);
  const {
    value: response_status_codes_value
  } = t12;
  let t13;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      strategy: "exponential",
      interval: 30000,
      count: 5,
      response_status_codes: null
    };
    $[8] = t13;
  } else {
    t13 = $[8];
  }
  const [retry_rules_config, setRetryRulesConfig] = useLocalStorage("pref:connection:rules-configs:retry", t13);
  let t14;
  if ($[9] !== count_value || $[10] !== interval_value || $[11] !== response_status_codes_value || $[12] !== setRetryRulesConfig || $[13] !== strategy_value) {
    t14 = () => {
      if (strategy_value && interval_value && count_value) {
        setRetryRulesConfig({
          strategy: strategy_value,
          interval: interval_value,
          count: count_value,
          response_status_codes: response_status_codes_value
        });
      }
    };
    $[9] = count_value;
    $[10] = interval_value;
    $[11] = response_status_codes_value;
    $[12] = setRetryRulesConfig;
    $[13] = strategy_value;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] !== count_value || $[16] !== interval_value || $[17] !== response_status_codes_value || $[18] !== strategy_value) {
    t15 = [strategy_value, interval_value, count_value, response_status_codes_value];
    $[15] = count_value;
    $[16] = interval_value;
    $[17] = response_status_codes_value;
    $[18] = strategy_value;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[20] !== dirty || $[21] !== resetForm || $[22] !== retry_rules_config || $[23] !== setValue || $[24] !== strategy_value || $[25] !== touched || $[26] !== value || $[27] !== values) {
    t16 = () => {
      if (strategy_value || touched) {
        return;
      }
      const new_value = {
        ...value,
        ...retry_rules_config
      };
      if (!dirty) {
        const new_values = {
          ...values,
          rules: values.rules.map(rule => rule.type === "retry" ? new_value : rule)
        };
        resetForm({
          values: new_values
        });
      } else {
        setValue(new_value);
      }
    };
    t17 = [strategy_value, touched, dirty, value, values, retry_rules_config, resetForm, setValue];
    $[20] = dirty;
    $[21] = resetForm;
    $[22] = retry_rules_config;
    $[23] = setValue;
    $[24] = strategy_value;
    $[25] = touched;
    $[26] = value;
    $[27] = values;
    $[28] = t16;
    $[29] = t17;
  } else {
    t16 = $[28];
    t17 = $[29];
  }
  useLayoutEffect(t16, t17);
  if (!strategy_value && !touched) {
    return null;
  }
  let t18;
  if ($[30] !== interval_value) {
    t18 = getMsIntervalByUnit(interval_value, "retry");
    $[30] = interval_value;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const [interval, unit] = t18;
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      x: 3,
      y: 2
    };
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      align: "center",
      wrap: true
    };
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Text text_wrap={false} m={{
      r: 1
    }}>Retry</Text>;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = Object.entries(RETRY_STATEGY_LABELS);
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== setStrategyValue) {
    t23 = t22.map(t24 => {
      const [value_0, label] = t24;
      return {
        label,
        onClick: () => setStrategyValue(value_0)
      };
    });
    $[36] = setStrategyValue;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== strategy_value) {
    t24 = (opened, toggle) => <Link as="button" type="button" active={opened} onClick={() => toggle(!opened)} style={{
      display: "inline-block"
    }}>{RETRY_STATEGY_LABELS[strategy_value].toLowerCase()}</Link>;
    $[38] = strategy_value;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t23 || $[41] !== t24) {
    t25 = <DropdownMenu.Permission options={t23} renderToggle={t24} />;
    $[40] = t23;
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Text text_wrap={false} m={{
      x: 1
    }}>every</Text>;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== interval || $[45] !== unit) {
    t27 = (opened_0, toggle_0) => <Link as="button" type="button" active={opened_0} onClick={() => toggle_0(!opened_0)}>{interval} {interval === 1 ? unit.slice(0, -1) : unit}</Link>;
    $[44] = interval;
    $[45] = unit;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      left: 2,
      b: 0
    };
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== name) {
    t29 = fieldName("interval", name);
    $[48] = name;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== t29) {
    t30 = <Div p={3}><IntervalInput m={t28} name={t29} label="Interval" placeholder="1" help="Delay between retries" required={true} /></Div>;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t27 || $[53] !== t30) {
    t31 = <Dropdown placement="bottom-start" renderToggle={t27}>{t30}</Dropdown>;
    $[52] = t27;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Text text_wrap={false} m={{
      x: 1
    }}>up to</Text>;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== count_value) {
    t33 = (opened_1, toggle_1) => <Link as="button" type="button" active={opened_1} onClick={() => toggle_1(!opened_1)} primary={true}>{count_value || 0} times</Link>;
    $[56] = count_value;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== name) {
    t34 = fieldName("count", name);
    $[58] = name;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== t34) {
    t35 = <Div p={3}><TextInput name={t34} label="Maximum Count" placeholder="10" help="Max of 50" type="number" max={50} min={1} required={true} m={0} /></Div>;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t33 || $[63] !== t35) {
    t36 = <Dropdown renderToggle={t33}>{t35}</Dropdown>;
    $[62] = t33;
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Text text_wrap={false} m={{
      x: 1
    }}>for</Text>;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== response_status_codes_value) {
    t38 = (opened_2, toggle_2) => <Link as="button" type="button" active={opened_2} onClick={() => toggle_2(!opened_2)} primary={true}>{httpCodesToSentence(response_status_codes_value)}</Link>;
    $[66] = response_status_codes_value;
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== name) {
    t39 = fieldName("response_status_codes", name);
    $[68] = name;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  let t40;
  if ($[70] !== t39) {
    t40 = <StyledCardSection p={3}><TextInput label="HTTP Code Rules" m={0} placeholder=">=300" name={t39} help="ie. >=300, !401, 500-599" /></StyledCardSection>;
    $[70] = t39;
    $[71] = t40;
  } else {
    t40 = $[71];
  }
  let t41;
  if ($[72] !== t38 || $[73] !== t40) {
    t41 = <Dropdown renderToggle={t38}>{t40}</Dropdown>;
    $[72] = t38;
    $[73] = t40;
    $[74] = t41;
  } else {
    t41 = $[74];
  }
  let t42;
  if ($[75] !== t25 || $[76] !== t31 || $[77] !== t36 || $[78] !== t41) {
    t42 = <StyledCardSection p={t19}><Div flex={t20}>{t21}{t25}{t26}{t31}{t32}{t36}{t37}{t41}</Div></StyledCardSection>;
    $[75] = t25;
    $[76] = t31;
    $[77] = t36;
    $[78] = t41;
    $[79] = t42;
  } else {
    t42 = $[79];
  }
  return t42;
};
const filter_keys: string[] = ['body', 'headers', 'query', 'path'];
const FilterRuleInputs: React.FC<{
  name: string;
  webhook_id?: string;
  source_id?: string;
  connection_name?: string;
  prefix: string;
  onBlur?: () => void;
}> = t0 => {
  const $ = _c(36);
  const {
    webhook_id,
    source_id,
    connection_name,
    prefix,
    onBlur
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const {
    values
  } = useFormikContext();
  let t1;
  if ($[0] !== values.rules) {
    t1 = values.rules.findIndex(_temp);
    $[0] = values.rules;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const index = t1;
  const name = `${prefix}[${index}]`;
  const value = values.rules[index];
  const [, t2] = useField(name);
  const {
    error
  } = t2;
  const [selected_filter_key, setFilterKey] = useLocalStorage("pref:connection:filter-rule:key", filter_keys && filter_keys[0]);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t3;
  if ($[2] !== HookdeckAPI || $[3] !== source_id || $[4] !== webhook_id) {
    const getLatestSampleData = async () => {
      if (webhook_id) {
        const {
          models
        } = await HookdeckAPI.events.list({
          webhook_id: [webhook_id],
          cli_id: {
            all: true
          },
          include: "data",
          limit: 1
        });
        return models[0]?.data ?? null;
      }
      if (source_id) {
        const {
          models: models_0
        } = await HookdeckAPI.requests.list({
          source_id,
          limit: 1
        });
        return models_0[0] ? (await HookdeckAPI.requests.get(models_0[0].id)).data ?? null : null;
      }
      return null;
    };
    t3 = async () => {
      const sample_data = await getLatestSampleData();
      return sample_data ? {
        headers: sample_data.headers,
        body: sample_data.body,
        query: sample_data.parsed_query,
        path: sample_data.path
      } : {};
    };
    $[2] = HookdeckAPI;
    $[3] = source_id;
    $[4] = webhook_id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getSampleInput = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      x: 3,
      t: 2
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== error || $[8] !== value) {
    t5 = filter_keys.map(key => ({
      key,
      label: capitalizeFirstLetter(key),
      badge_label: value[key] ? "1" : undefined,
      badge_theme: error && error[key] && !isFilterDescription(value[key]) ? "danger" : "muted"
    }));
    $[7] = error;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== setFilterKey) {
    t6 = key_0 => setFilterKey(key_0);
    $[10] = setFilterKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== selected_filter_key || $[13] !== t5 || $[14] !== t6) {
    t7 = <StyledCardSection p={t4}><Tabs compact={true} active_tab={selected_filter_key} tabs={t5} onTabSelected={t6} border={false} p={0} /></StyledCardSection>;
    $[12] = selected_filter_key;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = selected_filter_key as FilterSection;
  let t9;
  if ($[16] !== selected_filter_key || $[17] !== setFilterKey) {
    t9 = filters => {
      if (!filters[selected_filter_key]) {
        const section_0 = FILTER_SECTIONS.find(section => filters[section]);
        section_0 && setFilterKey(section_0);
      }
    };
    $[16] = selected_filter_key;
    $[17] = setFilterKey;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== getSampleInput || $[20] !== name || $[21] !== onBlur || $[22] !== t8 || $[23] !== t9) {
    t10 = <StyledCardSection><FilterEditorInput permission={true} hide_errors={true} format_on_blur={true} rule_name={name} section={t8} height="112px" getSampleInput={getSampleInput} onBlur={onBlur} onGenerated={t9} /></StyledCardSection>;
    $[19] = getSampleInput;
    $[20] = name;
    $[21] = onBlur;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== connection_name || $[26] !== name || $[27] !== query.filters || $[28] !== source_id || $[29] !== updateSearchQuery || $[30] !== webhook_id) {
    t11 = query.filters === name && <FilterRuleForm name={name} key={name} connection_name={connection_name} source_id={source_id} webhook_id={webhook_id} onClose={() => updateSearchQuery({}, {
      remove_keys: ["filters"]
    })} />;
    $[25] = connection_name;
    $[26] = name;
    $[27] = query.filters;
    $[28] = source_id;
    $[29] = updateSearchQuery;
    $[30] = webhook_id;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t7) {
    t12 = <>{t7}{t10}{t11}</>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t7;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};
const TransformRuleInputs: React.FC<{
  name: string;
  webhook_id?: string;
  rule: Rule;
  prefix: string;
  source_id?: string;
  display: Display;
}> = t0 => {
  const $ = _c(51);
  const {
    webhook_id,
    source_id,
    prefix
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    values,
    resetForm,
    dirty
  } = useFormikContext();
  let t1;
  if ($[0] !== values.rules) {
    t1 = values.rules.findIndex(_temp2);
    $[0] = values.rules;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const index = t1;
  const value = values.rules[index];
  const name = `${prefix}[${index}]`;
  const [,, t2] = useField(name);
  const {
    setValue
  } = t2;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = APIMethodKeys.transformations.list();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== HookdeckAPI.transformations) {
    t4 = () => HookdeckAPI.transformations.list();
    $[3] = HookdeckAPI.transformations;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: transformations
  } = useSWR(t3, t4);
  let t5;
  if ($[5] !== transformations?.models || $[6] !== value.transformation?.name || $[7] !== value.transformation_id) {
    t5 = transformations?.models.map(transformation => ({
      value: transformation.id,
      label: value.transformation_id === transformation.id ? value.transformation?.name || transformation.name : transformation.name
    })) || [];
    $[5] = transformations?.models;
    $[6] = value.transformation?.name;
    $[7] = value.transformation_id;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const transformations_options = t5;
  let t6;
  if ($[9] !== HookdeckAPI.transformations || $[10] !== dirty || $[11] !== resetForm || $[12] !== setValue || $[13] !== value || $[14] !== values) {
    t6 = () => {
      if (value.transformation_id) {
        HookdeckAPI.transformations.get(value.transformation_id).then(transformation_0 => {
          const new_value = {
            ...value,
            transformation: {
              name: transformation_0.name,
              code: transformation_0.code,
              env: Object.entries(transformation_0.env || {})
            }
          };
          if (!dirty) {
            const new_values = {
              ...values,
              rules: values.rules.map(rule_0 => rule_0.type === "transform" ? new_value : rule_0)
            };
            resetForm({
              values: new_values
            });
          } else {
            setValue(new_value);
          }
        });
      }
    };
    $[9] = HookdeckAPI.transformations;
    $[10] = dirty;
    $[11] = resetForm;
    $[12] = setValue;
    $[13] = value;
    $[14] = values;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== value.transformation_id) {
    t7 = [value.transformation_id];
    $[16] = value.transformation_id;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== name || $[19] !== setValue || $[20] !== updateSearchQuery) {
    t8 = () => <Div p={1}><Button w={100} ghost={true} icon="add_circle" onClick={() => {
        setValue({
          type: "transform",
          ...rules_default.transform
        });
        updateSearchQuery({
          transformation: name
        });
      }}>Create new transformation</Button></Div>;
    $[18] = name;
    $[19] = setValue;
    $[20] = updateSearchQuery;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let dropdown_props;
  let has_draft_transformation;
  if ($[22] !== t8 || $[23] !== value.transformation?.name || $[24] !== value.transformation_id) {
    dropdown_props = {
      getItemsFooter: t8
    } as any;
    has_draft_transformation = !value.transformation_id && !!value.transformation?.name;
    if (has_draft_transformation) {
      let t9;
      if ($[27] !== value.transformation?.name) {
        t9 = (opened, toggle) => <StyledSelect error={false} has_value={true} onClick={() => toggle(!opened)}><Button large={true} outline={true} block={true} flex={{
            justify: "space-between",
            align: "center"
          }}>{value?.transformation?.name}<Icon icon="expand_all" m={{
              r: 3
            }} style={{
              position: "absolute",
              cursor: "pointer",
              right: 0
            }} /></Button></StyledSelect>;
        $[27] = value.transformation?.name;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      dropdown_props.renderToggle = t9;
    }
    $[22] = t8;
    $[23] = value.transformation?.name;
    $[24] = value.transformation_id;
    $[25] = dropdown_props;
    $[26] = has_draft_transformation;
  } else {
    dropdown_props = $[25];
    has_draft_transformation = $[26];
  }
  let t9;
  if ($[29] !== dropdown_props || $[30] !== has_draft_transformation || $[31] !== name || $[32] !== setValue || $[33] !== transformations?.count || $[34] !== transformations_options || $[35] !== updateSearchQuery) {
    t9 = transformations?.count === 0 && !has_draft_transformation ? <Button outline={true} block={true} large={true} icon="add_circle" onClick={() => {
      setValue({
        type: "transform",
        ...rules_default.transform
      });
      updateSearchQuery({
        transformation: name
      });
    }}>Create first transformation</Button> : <SelectInput m={0} block={true} name={fieldName("transformation_id", name)} search={true} search_placeholder="Find a transformation..." dropdown={dropdown_props} options={transformations_options} />;
    $[29] = dropdown_props;
    $[30] = has_draft_transformation;
    $[31] = name;
    $[32] = setValue;
    $[33] = transformations?.count;
    $[34] = transformations_options;
    $[35] = updateSearchQuery;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const card = t9;
  let t10;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      b: 4,
      x: 4
    };
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== card) {
    t11 = <StyledCardSection borderless={true} p={t10}>{card}</StyledCardSection>;
    $[38] = card;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== name || $[41] !== query || $[42] !== setValue || $[43] !== source_id || $[44] !== updateSearchQuery || $[45] !== value || $[46] !== webhook_id) {
    t12 = query.transformation === name && <>{!value.transformation ? <Loading /> : <TransformRuleFormWrapper key={name} webhook_id={webhook_id} source_id={source_id} transformation_id={value.transformation_id} transformation={value.transformation} onClose={() => {
        updateSearchQuery({
          ...query,
          transform_tab: undefined,
          field: undefined,
          selected_execution_id: undefined,
          input_execution_id: undefined,
          transformation: undefined,
          transformation_id: undefined
        });
      }} onSave={async new_value_0 => {
        setValue({
          ...value,
          transformation: new_value_0.transformation
        });
        return Promise.resolve(true);
      }} />}</>;
    $[40] = name;
    $[41] = query;
    $[42] = setValue;
    $[43] = source_id;
    $[44] = updateSearchQuery;
    $[45] = value;
    $[46] = webhook_id;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t11 || $[49] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[48] = t11;
    $[49] = t12;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};
const DelayRuleInputs: React.FC<{
  name: string;
}> = t0 => {
  const $ = _c(24);
  const {
    name
  } = t0;
  const {
    values,
    resetForm,
    dirty
  } = useFormikContext();
  let t1;
  if ($[0] !== name) {
    t1 = fieldName("delay", name);
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: delay
  } = t2;
  const [t3, t4, t5] = useField(name);
  const {
    value
  } = t3;
  const {
    touched
  } = t4;
  const {
    setValue
  } = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      delay: 60000
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const [rule_config, setRulesConfig] = useLocalStorage("pref:connection:rules-configs:delay", t6);
  let t7;
  if ($[3] !== delay || $[4] !== setRulesConfig) {
    t7 = () => {
      if (delay) {
        setRulesConfig({
          delay
        });
      }
    };
    $[3] = delay;
    $[4] = setRulesConfig;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== delay) {
    t8 = [delay];
    $[6] = delay;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  useEffect(t7, t8);
  if (!delay && !touched) {
    const new_value = {
      ...value,
      ...rule_config
    };
    if (!dirty) {
      const new_values = {
        ...values,
        rules: values.rules.map(rule => rule.type === "delay" ? new_value : rule)
      };
      resetForm({
        values: new_values
      });
    } else {
      setValue(new_value);
    }
    return null;
  }
  let t9;
  if ($[8] !== delay) {
    t9 = getMsIntervalByUnit(delay, "delay");
    $[8] = delay;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const [delay_value, unit] = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      x: 3,
      y: 2
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      align: "center",
      wrap: true
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text text_wrap={false} m={{
      r: 1
    }}>Delay for</Text>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== delay_value || $[14] !== unit) {
    t13 = (opened, toggle) => <Link as="button" type="button" active={opened} onClick={() => toggle(!opened)}>{delay_value} {delay_value === 1 ? unit.slice(0, -1) : unit}</Link>;
    $[13] = delay_value;
    $[14] = unit;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      left: 2,
      b: 0
    };
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== name) {
    t15 = fieldName("delay", name);
    $[17] = name;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== t15) {
    t16 = <Div p={3}><IntervalInput m={t14} name={t15} label="Delay" placeholder="0" required={true} /></Div>;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t13 || $[22] !== t16) {
    t17 = <StyledCardSection p={t10}><Div flex={t11}>{t12}<Dropdown parent_width={false} placement="bottom-start" renderToggle={t13}>{t16}</Dropdown></Div></StyledCardSection>;
    $[21] = t13;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  return t17;
};
const DeduplicateRuleInputs: React.FC<{
  name: string;
  display: Display;
  webhook_id?: string;
  source_id?: string;
}> = ({
  name,
  webhook_id,
  source_id
}) => {
  const [{
    value: window_value
  }] = useField(fieldName('window', name));
  const [{
    value: field_mode
  },, {
    setValue: setFieldMode
  }] = useField(fieldName('field_mode', name));
  const [window_interval, unit] = getMsIntervalByUnit(window_value, 'deduplicate');

  // Create a hook that provides the required parameters
  const useSuggestionsWithParams = () => useEventPayloadFieldsSuggestions({
    webhook_id,
    source_id
  });
  return <>
      <StyledCardSection p={{
      x: 4,
      y: 3
    }}>
        <Div flex={{
        direction: 'column',
        gap: 1
      }}>
          <Div flex={{
          align: 'center',
          wrap: true
        }}>
            <Text text_wrap={false} m={{
            r: 1
          }}>
              Deduplicate events when
            </Text>
            <DropdownMenu renderToggle={(opened, toggle) => <Link as="button" type="button" active={opened} onClick={() => toggle(!opened)} primary>
                  {field_mode === 'all' || !field_mode ? 'all event data' : field_mode === 'include' ? 'specific fields' : 'all event data, excluding specific fields'}
                </Link>} options={[{
            label: 'All event data',
            selected: field_mode === 'all',
            onClick: () => {
              setFieldMode('all');
            }
          }, {
            label: 'Specific fields',
            selected: field_mode === 'include',
            onClick: () => {
              setFieldMode('include');
            }
          }, {
            label: 'All event data, excluding specific fields',
            selected: field_mode === 'exclude',
            onClick: () => {
              setFieldMode('exclude');
            }
          }]} />
            <Text text_wrap={false} m={{
            x: 1
          }}>
              match{field_mode === 'include' ? '' : 'es'} within
            </Text>
            <Dropdown parent_width={false} placement="bottom-start" renderToggle={(opened_0, toggle_0) => <Link as="button" type="button" active={opened_0} onClick={() => toggle_0(!opened_0)}>
                  {window_interval} {window_interval === 1 ? unit.slice(0, -1) : unit}
                </Link>}>
              <Div p={3}>
                <IntervalInput m={{
                left: 2,
                b: 0
              }} name={fieldName('window', name)} label="Window" placeholder="5" required max_interval="hours" />
              </Div>
            </Dropdown>
          </Div>
        </Div>
      </StyledCardSection>
      {(field_mode === 'include' || field_mode === 'exclude') && <StyledCardSection p={{
      x: 4,
      y: 3
    }} muted>
          <JSONFieldSelectorInput name={fieldName('fields', name)} required label={field_mode === 'include' ? 'Matching fields' : 'Excluded fields'} placeholder="Enter payload fields, e.g. 'body.id'" useSuggestions={useSuggestionsWithParams} />
        </StyledCardSection>}
    </>;
};
const COMPONENTS = {
  retry: RetryRuleInputs,
  filter: FilterRuleInputs,
  transform: TransformRuleInputs,
  delay: DelayRuleInputs,
  deduplicate: DeduplicateRuleInputs
};
const StyledRulesWrapper = styled.div<{
  $is_dragging?: boolean;
}>`
  position: relative;
  ${({
  $is_dragging
}) => $is_dragging && 'isolation: isolate;'}
`;

// Sortable Rule Item Component
const SortableRuleItem: React.FC<{
  id: string;
  rule: Rule | null;
  type: string;
  prefix: string;
  name: string;
  webhook_id?: string;
  source_id?: string;
  connection_name?: string;
  display: 'full' | 'compact';
  onAdd: (type: string) => void;
  onRemove: (type: string) => void;
  updateSearchQuery: (query: any) => void;
  submitCount: number;
  error: any;
  touched: boolean;
  is_draggable: boolean;
  isDragOverlay?: boolean;
}> = t0 => {
  const $ = _c(95);
  const {
    id,
    rule,
    type,
    prefix,
    name,
    webhook_id,
    source_id,
    connection_name,
    display,
    onAdd,
    onRemove,
    updateSearchQuery,
    submitCount,
    error,
    touched,
    is_draggable,
    isDragOverlay: t1
  } = t0;
  const isDragOverlay = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== id || $[1] !== isDragOverlay) {
    t2 = {
      id,
      disabled: isDragOverlay
    };
    $[0] = id;
    $[1] = isDragOverlay;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: is_dragging
  } = useSortable(t2);
  let t3;
  if ($[3] !== isDragOverlay || $[4] !== is_dragging || $[5] !== transform || $[6] !== transition) {
    t3 = isDragOverlay ? {} : {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: is_dragging ? 0.5 : 1
    };
    $[3] = isDragOverlay;
    $[4] = is_dragging;
    $[5] = transform;
    $[6] = transition;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const style = t3;
  const Component = COMPONENTS[type];
  const show_editor_button = "filter" === type && !!rule || "transform" === type && rule?.type === "transform" && (rule as any)?.transformation?.name;
  let t4;
  if ($[8] !== name || $[9] !== type) {
    t4 = type === "filter" ? {
      filters: name
    } : {
      transformation: name
    };
    $[8] = name;
    $[9] = type;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const editor_search_query = t4;
  const [is_filter_blurred, setFilterBlurred] = useState(false);
  let t5;
  let t6;
  if ($[11] !== rule) {
    t5 = () => {
      if (!rule) {
        setFilterBlurred(false);
      }
    };
    t6 = [rule];
    $[11] = rule;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== error || $[15] !== rule || $[16] !== type) {
    t7 = type === "filter" && error && typeof error === "object" ? Object.fromEntries(Object.entries(error).filter(t8 => {
      const [section] = t8;
      return !isFilterDescription(rule?.[section]);
    })) : null;
    $[14] = error;
    $[15] = rule;
    $[16] = type;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const filter_errors = t7;
  let t8;
  if ($[18] !== error || $[19] !== filter_errors) {
    t8 = filter_errors ? Object.keys(filter_errors).length > 0 ? filter_errors : null : error;
    $[18] = error;
    $[19] = filter_errors;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const displayed_error = t8;
  const t9 = is_draggable && !isDragOverlay ? setNodeRef : undefined;
  const t10 = !rule;
  const t11 = type === "filter" ? true : undefined;
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      y: 3,
      x: 4
    };
    t13 = {
      align: "center",
      justify: "space-between"
    };
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      align: "center",
      gap: 3
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = !!rule;
  let t16;
  if ($[24] !== onAdd || $[25] !== onRemove || $[26] !== rule || $[27] !== type) {
    t16 = () => rule ? onRemove(type) : onAdd(type);
    $[24] = onAdd;
    $[25] = onRemove;
    $[26] = rule;
    $[27] = type;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <Switch checked={t15} onChange={t16} />;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      align: "center",
      gap: 2
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = RULE_TYPES[type];
  let t20;
  if ($[33] !== t19.icon) {
    t20 = <Icon icon={t19.icon} muted={true} />;
    $[33] = t19.icon;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const t21 = !rule;
  const t22 = RULE_TYPES[type];
  let t23;
  if ($[35] !== t21 || $[36] !== t22.label) {
    t23 = <Text semi={true} muted={t21}>{t22.label}</Text>;
    $[35] = t21;
    $[36] = t22.label;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  const t24 = RULE_TYPES[type];
  const t25 = RULE_TYPES[type];
  let t26;
  if ($[38] !== t25.docs) {
    t26 = {
      label: "Learn more",
      to: t25.docs
    };
    $[38] = t25.docs;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Icon pointer={true} icon="info" muted={true} />;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] !== t24.description || $[42] !== t26) {
    t28 = <Tooltip tooltip={t24.description} cta={t26}>{t27}</Tooltip>;
    $[41] = t24.description;
    $[42] = t26;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t20 || $[45] !== t23 || $[46] !== t28) {
    t29 = <Div flex={t18}>{t20}{t23}{t28}</Div>;
    $[44] = t20;
    $[45] = t23;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== t17 || $[49] !== t29) {
    t30 = <Div flex={t14}>{t17}{t29}</Div>;
    $[48] = t17;
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      align: "center",
      gap: 3
    };
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== editor_search_query || $[53] !== show_editor_button || $[54] !== updateSearchQuery) {
    t32 = show_editor_button && <Button icon="open" ghost={true} small={true} m={{
      y: -2
    }} onClick={() => updateSearchQuery(editor_search_query)}>Editor</Button>;
    $[52] = editor_search_query;
    $[53] = show_editor_button;
    $[54] = updateSearchQuery;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== is_draggable || $[57] !== is_dragging || $[58] !== listeners) {
    t33 = is_draggable && <Div {...listeners} style={{
      cursor: is_dragging ? "grabbing" : "grab"
    }} p={{
      r: 1
    }} title="Drag to reorder"><Icon icon="drag_indicator" neutral={true} style={{
        cursor: is_dragging ? "grabbing" : "grab"
      }} /></Div>;
    $[56] = is_draggable;
    $[57] = is_dragging;
    $[58] = listeners;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t32 || $[61] !== t33) {
    t34 = <Div flex={t31}>{t32}{t33}</Div>;
    $[60] = t32;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t30 || $[64] !== t34) {
    t35 = <StyledCardSection p={t12} flex={t13}>{t30}{t34}</StyledCardSection>;
    $[63] = t30;
    $[64] = t34;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== Component || $[67] !== connection_name || $[68] !== display || $[69] !== name || $[70] !== onAdd || $[71] !== onRemove || $[72] !== prefix || $[73] !== rule || $[74] !== source_id || $[75] !== type || $[76] !== webhook_id) {
    t36 = rule && <Component name={name} prefix={prefix} enabled={!!rule} rule={rule} onEnable={() => onAdd(type)} onDisable={() => onRemove(type)} webhook_id={webhook_id} source_id={source_id} connection_name={connection_name} display={display} onBlur={() => setFilterBlurred(true)} />;
    $[66] = Component;
    $[67] = connection_name;
    $[68] = display;
    $[69] = name;
    $[70] = onAdd;
    $[71] = onRemove;
    $[72] = prefix;
    $[73] = rule;
    $[74] = source_id;
    $[75] = type;
    $[76] = webhook_id;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t10 || $[79] !== t11 || $[80] !== t35 || $[81] !== t36) {
    t37 = <StyledCard dashed={t10} overflow_hidden={t11}>{t35}{t36}</StyledCard>;
    $[78] = t10;
    $[79] = t11;
    $[80] = t35;
    $[81] = t36;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== displayed_error || $[84] !== is_filter_blurred || $[85] !== submitCount || $[86] !== touched || $[87] !== type) {
    t38 = displayed_error && (type === "filter" ? is_filter_blurred || submitCount > 0 : touched && (type !== "transform" && type !== "deduplicate" || submitCount > 0)) && <Text m={{
      top: 1.5,
      bottom: 0
    }} as="p" danger={true}>{typeof displayed_error === "object" ? Object.values(displayed_error)[0] as string : String(displayed_error)}</Text>;
    $[83] = displayed_error;
    $[84] = is_filter_blurred;
    $[85] = submitCount;
    $[86] = touched;
    $[87] = type;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  let t39;
  if ($[89] !== attributes || $[90] !== style || $[91] !== t37 || $[92] !== t38 || $[93] !== t9) {
    t39 = <Div ref={t9} style={style} {...attributes}>{t37}{t38}</Div>;
    $[89] = attributes;
    $[90] = style;
    $[91] = t37;
    $[92] = t38;
    $[93] = t9;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  return t39;
};
export type RulesFormValues = Rule[];
const rule_form_props = {
  postprocessValues: (values: RulesFormValues) => {
    // deep copy the values so they don't change in the form
    values = JSON.parse(JSON.stringify(values));
    return postprocessFormRules(values);
  },
  getInitialValues: (rules?: Rule[], transformation_id?: string) => {
    const default_values = [{
      type: 'retry',
      ...rules_default.retry
    }] as Rule[];
    if (transformation_id) {
      default_values.push({
        type: 'transform',
        transformation_id
      });
    }
    return rules ? preprocessFormRules(rules) : default_values;
  },
  validate: async (values: RulesFormValues, HookdeckAPI: HookdeckAPI) => {
    const rules_error = await validateFormRules(values, HookdeckAPI);
    if (rules_error) {
      return rules_error;
    }
    return {};
  },
  Fields: ({
    prefix,
    webhook_id,
    source_id,
    display,
    connection_name
  }: {
    prefix: string;
    webhook_id?: string;
    source_id?: string;
    display: 'full' | 'compact';
    connection_name?: string;
  }) => {
    const {
      HookdeckAPI
    } = useContext(GlobalContext);
    const {
      validateField,
      submitCount
    } = useFormikContext();
    const {
      updateSearchQuery
    } = useSearchQuery<{
      filters: Boolean;
    }>();
    const [{
      value: rules
    }, {
      error,
      touched
    }, {
      setValue,
      setTouched
    }] = useField(prefix);

    // State to track the order of draggable rules
    const [draggable_rules_order, setDraggableRulesOrder] = useState<string[]>(() => {
      const result: string[] = [];
      const active_rules = rules.map(({
        type
      }) => type).filter(type => DRAGGABLE_RULES.includes(type));
      const processed = new Set();
      let last_index = 0;

      // Process each item from the subset array
      for (const item of active_rules) {
        // Find the position of this item in the original array
        const indexInAll = DRAGGABLE_RULES.indexOf(item);

        // Add any items that come before this one in the original array
        // that haven't been processed yet
        for (let i = last_index; i < indexInAll; i++) {
          if (!processed.has(DRAGGABLE_RULES[i]) && !active_rules.includes(DRAGGABLE_RULES[i])) {
            result.push(DRAGGABLE_RULES[i]);
            processed.add(DRAGGABLE_RULES[i]);
          }
        }

        // Add the current subset item
        result.push(item);
        processed.add(item);

        // Update the last index to continue from
        last_index = Math.max(last_index, indexInAll + 1);
      }

      // Add any remaining items from the original array
      for (let i = last_index; i < DRAGGABLE_RULES.length; i++) {
        if (!processed.has(DRAGGABLE_RULES[i])) {
          result.push(DRAGGABLE_RULES[i]);
          processed.add(DRAGGABLE_RULES[i]);
        }
      }
      return result;
    });

    // State to track the active drag item for DragOverlay
    const [active_id, setActiveId] = useState<string | null>(null);

    // Set up drag and drop sensors
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    }));

    // Helper function to enforce the correct rule order: draggable rules, then delay, then retry
    const enforceRuleOrder = (rules: Rule[], custom_draggable_order?: string[]) => {
      const ordered_rules: Rule[] = [];
      const order_to_use = custom_draggable_order || draggable_rules_order;

      // Add draggable rules in the specified order
      for (const type of order_to_use) {
        const rule = rules.find(r => r.type === type);
        if (rule) {
          ordered_rules.push(rule);
        }
      }

      // Add delay rule
      const delay_rule = rules.find(r => r.type === 'delay');
      if (delay_rule) {
        ordered_rules.push(delay_rule);
      }

      // Add retry rule
      const retry_rule = rules.find(r => r.type === 'retry');
      if (retry_rule) {
        ordered_rules.push(retry_rule);
      }
      return ordered_rules;
    };
    const onSetValue = v => {
      setTouched(true);
      setValue(v);
      validateField(prefix);
    };
    const addType = type => {
      if (!rules.some(r => r.type === type)) {
        const new_rules = [...rules, {
          type,
          ...rules_default[type]
        }];
        // Enforce the correct order after adding
        onSetValue(enforceRuleOrder(new_rules));
        if (type === 'filter') {
          updateSearchQuery({
            filters: true
          });
        }
      }
    };
    const removeType = type => {
      if (rules.some(r => r.type === type)) {
        onSetValue(rules.filter(r => r.type !== type));
      }
    };

    // Handle drag start event
    const handleDragStart = (event: DragStartEvent) => {
      setActiveId(event.active.id as string);
    };

    // Handle drag end event
    const handleDragEnd = (event: DragEndEvent) => {
      const {
        active,
        over
      } = event;
      setActiveId(null);
      if (over && active.id !== over.id) {
        // Update the draggable rules order
        const old_index = draggable_rules_order.findIndex(type => type === active.id as string);
        const new_index = draggable_rules_order.findIndex(type => type === over.id as string);
        if (old_index !== -1 && new_index !== -1) {
          const new_order = arrayMove(draggable_rules_order, old_index, new_index);
          setDraggableRulesOrder(new_order);

          // If both rules exist in the actual rules array, reorder them there too
          const active_rule = rules.find(rule => rule.type === active.id as string);
          const over_rule = rules.find(rule => rule.type === over.id as string);
          if (active_rule && over_rule) {
            // Update the draggable_rules_order first, then enforce the complete rule order
            setDraggableRulesOrder(new_order);

            // Enforce the correct order using the helper function with the new order
            onSetValue(enforceRuleOrder(rules, new_order));
          }
        }
      }
    };
    const rendering_order = getRenderingOrder(draggable_rules_order);

    // Preload transformations TODO: Remove once we upgrade SWR to v2
    useSWR(APIMethodKeys.transformations.list(), () => HookdeckAPI.transformations.list());
    const sortable_rules_elements: [string, React.ReactNode][] = rendering_order.map(key => {
      const rule_index = rules.findIndex(r => r.type === key);
      const rule = rules[rule_index];
      const name = `${prefix}[${rule_index}]`;
      const rule_error = error && error[rule_index];
      return [key, <SortableRuleItem key={key} id={key} rule={rule} type={key} prefix={prefix} name={name} webhook_id={webhook_id} source_id={source_id} connection_name={connection_name} display={display} onAdd={addType} onRemove={removeType} updateSearchQuery={updateSearchQuery} submitCount={submitCount} error={rule_error} touched={touched} is_draggable={DRAGGABLE_RULES.includes(key)} />];
    });
    const sortable_active_rule_element = active_id ? sortable_rules_elements.find(([key]) => key === active_id) : null;
    return <StyledRulesWrapper $is_dragging={!!active_id}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext items={draggable_rules_order} strategy={verticalListSortingStrategy}>
            <Div flex={{
            direction: 'column',
            gap: 4
          }}>
              {sortable_rules_elements.map(([key, element]) => <React.Fragment key={key}>
                  {element}
                  {draggable_rules_order.includes(key) && key !== draggable_rules_order[draggable_rules_order.length - 1] && <Div w={100} m={{
                y: -4.25
              }} flex={{
                align: 'center',
                justify: 'center',
                direction: 'column'
              }} style={{
                zIndex: 2
              }}>
                        <Div h={{
                  px: 16
                }}>
                          <svg width="6" height="16" viewBox="0 0 6 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 16L5.88675 11L0.113249 11L3 16ZM3 0L2.5 2.18557e-08L2.5 11.5L3 11.5L3.5 11.5L3.5 -2.18557e-08L3 0Z" fill="#CCCCCC" />
                          </svg>
                        </Div>
                      </Div>}
                </React.Fragment>)}
            </Div>
          </SortableContext>
          {sortable_active_rule_element && createPortal(<DragOverlay style={{
          zIndex: 2000
        }}>{sortable_active_rule_element[1]}</DragOverlay>, document.body)}
        </DndContext>
      </StyledRulesWrapper>;
  }
};
export default rule_form_props;
function _temp(rule) {
  return rule.type === "filter";
}
function _temp2(rule) {
  return rule.type === "transform";
}